import React, { useState, useEffect } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { TextField, MenuItem } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmNotice, CrmFilterBar, CrmRowActions, ListTruncate, ListLink } from "../shared/CrmUi";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { appConfirm } from "components/AppDialog";


function OpportunityTable() {
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const [salesReps, setSalesReps] = useState(["All"]); // seçili "All" yüklemeden önce de listede olsun
    const [statusList, setStatusList] = useState(["All"]);
    const [selectedSalesRep, setSelectedSalesRep] = useState("All");
    const [selectedStatus, setSelectedStatus] = useState("All");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const navigate = useNavigate();

    const statusMap = {
        Prospect:           { barValue: 20,  labelValue: 20,  color: "#FFB300" },
        "Needs Assessment": { barValue: 50,  labelValue: 50,  color: "#8E24AA" },
        Target:             { barValue: 70,  labelValue: 70,  color: "#053b97" },
        Secured:            { barValue: 100, labelValue: 100, color: "#4CAF50" },
        Lost:               { barValue: 5,   labelValue: 0,   color: "#F44336" }, // özel kural
    };
    const statusOptions = React.useMemo(
        () => Object.keys(statusMap).map((label) => ({ label })),
        []
    );
    const StatusDot = ({ status, size = 10 }) => (
        <span
            style={{
                width: size,
                height: size,
                borderRadius: 999,
                display: "inline-block",
                marginRight: 8,
                backgroundColor: statusMap[status]?.color || "#999",
                flexShrink: 0,
            }}
        />
    );


    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await CrudService.getOpportunities();
                const normalizedData = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalizedData);
                const reps = new Set();
                const statuses = new Set();
                normalizedData.forEach((item) => {
                    const rep = item.salesRep;
                    if (Array.isArray(rep)) {
                        rep.forEach((r) => r?.name && reps.add(r.name));
                    } else if (typeof rep === "object") {
                        rep?.name && reps.add(rep.name);
                    } else {
                        reps.add(rep);
                    }
                    item.status && statuses.add(item.status);
                });

                setSalesReps(["All", ...Array.from(reps)]);
                setStatusList(["All", ...Array.from(statuses)]);
            } catch (error) {
                setNotification({
                    value: true,
                    text: "Failed to load opportunities. Please try again later.",
                });
            }
        };
        fetchData();
    }, []);

    const patchOpportunityStatusLocal = (id, newStatus) => {
        setData((prev) =>
            prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
        );
    };

    const StatusCell = React.memo(function StatusCell({ opportunityId, currentStatus }) {
        const [saving, setSaving] = useState(false);

        const currentOption =
            statusOptions.find((o) => o.label === currentStatus) ?? null;

        const handleChange = async (_, newValue) => {
            const nextStatus = newValue?.label ?? "";
            if (!nextStatus || nextStatus === currentStatus) return;

            const prevStatus = currentStatus;

            // Optimistic update
            patchOpportunityStatusLocal(opportunityId, nextStatus);

            setSaving(true);
            try {
                // ✅ 1) Eğer böyle bir endpoint'in varsa:
                // await CrudService.updateOpportunityStatus(opportunityId, { status: nextStatus });

                // ✅ 2) Yoksa, genelde sende bu pattern var:
                await CrudService.updateOpportunity({ status: nextStatus }, opportunityId);

                setNotification({ value: true, text: "Status updated successfully." });
                setTimeout(() => setNotification({ value: false, text: "" }), 2500);
            } catch (e) {
                // Rollback
                patchOpportunityStatusLocal(opportunityId, prevStatus);
                setNotification({ value: true, text: "Failed to update status." });
                setTimeout(() => setNotification({ value: false, text: "" }), 4000);
                console.error(e);
            } finally {
                setSaving(false);
            }
        };
        return (
            <Autocomplete
                options={statusOptions}
                value={currentOption}
                onChange={handleChange}
                disableClearable
                size="small"
                isOptionEqualToValue={(opt, val) => opt.label === val.label}
                getOptionLabel={(opt) => opt.label}
                sx={{ minWidth: 200 }}

                // ✅ Dropdown option'lar dot'lu
                renderOption={({ key: optionKey, ...props }, option) => (
                    <li {...props} key={option.label}>
        <span style={{ display: "flex", alignItems: "center" }}>
          <StatusDot status={option.label} />
            {option.label}
        </span>
                    </li>
                )}

                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Status"
                        InputProps={{
                            ...params.InputProps,

                            // ✅ Seçili değerde dot
                            startAdornment: (
                                <span style={{ display: "flex", alignItems: "center" }}>
              <StatusDot status={currentStatus} />
            </span>
                            ),

                            endAdornment: (
                                <>
                                    {saving ? <CircularProgress size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

        );
    });


    const getRows = (rawData) => {
        return rawData
            .filter((op) => {
                const estDate = dayjs(op.estimatedCloseDate);
                if (startDate && endDate && (estDate.isBefore(startDate, "day") || estDate.isAfter(endDate, "day"))) {
                    return false;}
                if (selectedSalesRep !== "All") {
                    const rep = op.salesRep;
                    if (Array.isArray(rep)) {
                        if (!rep.some((r) => r?.name === selectedSalesRep)) return false;
                    } else if ((rep?.name || rep) !== selectedSalesRep) {
                        return false;
                    }
                }
                if (selectedStatus !== "All" && op.status !== selectedStatus) {
                    return false;}
                return true;
            })
            .map((opportunity) => ({
                id: opportunity.id,
                selectedTransportType: opportunity.selectedTransportType,
                customer: (
                    <ListLink onClick={() => clickEditHandler(opportunity.id)}><ListTruncate text={opportunity.customer || "—"} max={40} /></ListLink>
                ),
                estimatedCloseDate: dayjs(opportunity.estimatedCloseDate).isValid()
                    ? dayjs(opportunity.estimatedCloseDate).format("DD.MM.YYYY")
                    : "-",
                status: (
                    <StatusCell
                        opportunityId={opportunity.id}
                        currentStatus={opportunity.status}
                    />
                ),
                salesRep: Array.isArray(opportunity.salesRep)
                    ? opportunity.salesRep.map((r) => r?.name).join(", ")
                    : typeof opportunity.salesRep === "object"
                        ? opportunity.salesRep?.name
                        : opportunity.salesRep || "Unknown",
                actions: (
                    <CrmRowActions onEdit={() => clickEditHandler(opportunity.id)} editTitle="Edit opportunity" onDelete={(e) => clickDeleteHandler(e, opportunity.id)} deleteTitle="Delete opportunity" />
                ),
            }));
    };

    useEffect(() => {
        setTableData(getRows(data));
    }, [data, selectedSalesRep, selectedStatus, startDate, endDate]);

    const clickEditHandler = (id) => {navigate(`/create-opportunity/details?id=${id}`);};
    const clickDeleteHandler = async (e, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this opportunity?")) return;
            await CrudService.deleteOpportunity(id);
            setData((prev) => prev.filter((item) => item.id !== id));
            setNotification({ value: true, text: "Opportunity deleted successfully." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        } catch (error) {
            setNotification({ value: true, text: "Failed to delete opportunity." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };
    const clickAddHandler = () => navigate("/create-opportunity");
    const dataTableData = {
        columns: [
            { Header: "Transport Type", accessor: "selectedTransportType" },
            { Header: "Customer", accessor: "customer" },
            { Header: "Est Close Date", accessor: "estimatedCloseDate" },
            { Header: "Opportunity Owner", accessor: "salesRep" },
            { Header: "Status", accessor: "status" },
            { Header: "Actions", accessor: "actions", disableSortBy: true },
        ],
        rows: tableData,
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Opportunities"
                    subtitle="Open sales opportunities by sales representative, status and estimated close date."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            Create Opportunity
                        </CrmActionButton>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <CrmFilterBar>
                            <DatePicker
                                label="Start Date"
                                format="DD.MM.YYYY"
                                value={startDate}
                                onChange={setStartDate}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        sx: {
                                            minWidth: 200,
                                            "& .MuiInputBase-root": {
                                                height: 40,
                                            },
                                        },
                                    },
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                format="DD.MM.YYYY"
                                value={endDate}
                                onChange={setEndDate}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        sx: {
                                            minWidth: 200,
                                            "& .MuiInputBase-root": {
                                                height: 40,
                                            },
                                        },
                                    },
                                }}
                            />
                            <TextField
                                select
                                variant="outlined"
                                label="Sales Rep"
                                value={selectedSalesRep}
                                onChange={(e) => setSelectedSalesRep(e.target.value)}
                                sx={{
                                    minWidth: 200,
                                    "& .MuiInputBase-root": {
                                        height: 40,
                                        alignItems: "center",
                                    },
                                }}
                            >
                                {salesReps.map((rep) => (
                                    <MenuItem key={rep} value={rep}>
                                        {rep}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                variant="outlined"
                                label="Status"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                sx={{
                                    minWidth: 200,
                                    "& .MuiInputBase-root": {
                                        height: 40,
                                        alignItems: "center",
                                    },
                                }}
                            >
                                {statusList.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </CrmFilterBar>
                    </LocalizationProvider>
                <CrmTableCard title="Opportunity List" summary={`${tableData.length} opportunities`}>
                    <DataTable table={dataTableData} canSearch />
                </CrmTableCard>
            </CrmPage>
            <Footer />
        </DashboardLayout>
    );
}

export default OpportunityTable;
